import {
  DocumentationItem,
  DocumentationListElementsResponse,
  DocumentationSearchResponse,
  DocumentationDependenciesResponse,
  DocumentationDependentsResponse,
} from './documentation';

export enum WorkatoElementType {
  PROJECT = 'workato.project',
  RECIPE = 'workato.recipe',
  APP = 'workato.app',
  CONNECTION = 'workato.connection',
  CONNECTOR = 'workato.connector',
  FOLDER = 'workato.folder',
  JOB = 'workato.job',
  COLLABORATOR = 'workato.collaborator',
  GENIE = 'workato.genie',
  SKILL = 'workato.skill',
  KNOWLEDGE_BASE = 'workato.knowledge_base',
  MCP_SERVER = 'workato.mcp_server',
  LOOKUP_TABLE = 'workato.lookup_table',
  DATA_TABLE = 'workato.data_table',
  EVENT_TOPIC = 'workato.event_topic',
  CUSTOM_CONNECTOR = 'workato.custom_connector',
  AUDIT_LOG = 'workato.audit_log',
  TAG = 'workato.tag',
  ENV_PROPERTY = 'workato.env_property',
  MESSAGE_TEMPLATE = 'workato.message_template',
  CUSTOM_OAUTH_PROFILE = 'workato.custom_oauth_profile',
  COLLABORATOR_GROUP = 'workato.collaborator_group',
  ENV_ROLE = 'workato.env_role',
  PROJECT_ROLE = 'workato.project_role',
  LEGACY_ROLE = 'workato.legacy_role',
  DEPLOYMENT = 'workato.deployment',
  PROJECT_GRANT = 'workato.project_grant',
  GROUP_MEMBER = 'workato.group_member',
}

export type WorkatoTabType = WorkatoElementType;

export interface WorkatoItemBase<T extends WorkatoElementType = WorkatoElementType>
  extends DocumentationItem {
  parentType: T;
  description?: string;
  body?: string;
}

export interface WorkatoProjectItem extends WorkatoItemBase<WorkatoElementType.PROJECT> {
  folderId?: string;
}

export interface WorkatoRecipeItem extends WorkatoItemBase<WorkatoElementType.RECIPE> {
  projectId?: string;
  folderId?: string;
  userId?: string;
  running?: string;
  versionNo?: string;
  triggerApplication?: string;
  actionApplications?: string;
  authorName?: string;
  createdAt?: string;
  updatedAt?: string;
  lastRunAt?: string;
  tags?: string;
}

export type WorkatoAppItem = WorkatoItemBase<WorkatoElementType.APP>;

export interface WorkatoConnectionItem extends WorkatoItemBase<WorkatoElementType.CONNECTION> {
  provider?: string;
  accountId?: string;
  application?: string;
  connectionId?: string;
  authorizationStatus?: string;
  authorizationError?: string;
  authorizedAt?: string;
  connectionLostAt?: string;
  connectionLostReason?: string;
  folderId?: string;
  projectId?: string;
  parentId?: string;
  externalId?: string;
  createdAt?: string;
  updatedAt?: string;
  tags?: string;
}

export interface WorkatoConnectorItem extends WorkatoItemBase<WorkatoElementType.CONNECTOR> {
  provider?: string;
  title?: string;
  categories?: string;
  oauth?: string;
  deprecated?: string;
  secondary?: string;
}

export interface WorkatoFolderItem extends WorkatoItemBase<WorkatoElementType.FOLDER> {
  parentId?: string;
  isProject?: string;
  projectId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface WorkatoJobItem extends WorkatoItemBase<WorkatoElementType.JOB> {
  recipeId?: string;
  status?: string;
  title?: string;
  completedAt?: string;
  startedAt?: string;
  isError?: string;
  error?: string;
}

export interface WorkatoCollaboratorItem extends WorkatoItemBase<WorkatoElementType.COLLABORATOR> {
  email?: string;
  /** Flattened display string derived from `environmentRoles` (env → role). */
  role?: string;
  /** Per-environment role names from GET /api/members `roles[]` (environment_type → role_name). */
  environmentRoles?: Record<string, string>;
  /** Workspace grant type from `grant_type` (`team`, `federation_manager`). */
  grantType?: string;
  timeZone?: string;
  createdAt?: string;
  externalId?: string;
  userGroups?: string;
}

export type WorkatoGenieItem = WorkatoItemBase<WorkatoElementType.GENIE>;
export type WorkatoSkillItem = WorkatoItemBase<WorkatoElementType.SKILL>;
export type WorkatoKnowledgeBaseItem = WorkatoItemBase<WorkatoElementType.KNOWLEDGE_BASE>;
export type WorkatoMcpServerItem = WorkatoItemBase<WorkatoElementType.MCP_SERVER>;
export type WorkatoLookupTableItem = WorkatoItemBase<WorkatoElementType.LOOKUP_TABLE>;
export type WorkatoDataTableItem = WorkatoItemBase<WorkatoElementType.DATA_TABLE>;
export type WorkatoEventTopicItem = WorkatoItemBase<WorkatoElementType.EVENT_TOPIC>;
export type WorkatoCustomConnectorItem = WorkatoItemBase<WorkatoElementType.CUSTOM_CONNECTOR>;
export type WorkatoAuditLogItem = WorkatoItemBase<WorkatoElementType.AUDIT_LOG>;
export interface WorkatoTagItem extends WorkatoItemBase<WorkatoElementType.TAG> {
  handle?: string;
  title?: string;
  color?: string;
  assignmentCount?: string;
  authorId?: string;
  authorName?: string;
  createdAt?: string;
  updatedAt?: string;
}
export type WorkatoEnvPropertyItem = WorkatoItemBase<WorkatoElementType.ENV_PROPERTY>;
export interface WorkatoMessageTemplateItem
  extends WorkatoItemBase<WorkatoElementType.MESSAGE_TEMPLATE> {
  version?: string;
  folder?: string;
  absolutePath?: string;
  zipName?: string;
}
export type WorkatoCustomOAuthProfileItem =
  WorkatoItemBase<WorkatoElementType.CUSTOM_OAUTH_PROFILE>;
export type WorkatoCollaboratorGroupItem = WorkatoItemBase<WorkatoElementType.COLLABORATOR_GROUP>;
export interface WorkatoEnvRoleItem extends WorkatoItemBase<WorkatoElementType.ENV_ROLE> {
  membersCount?: string;
  roleType?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface WorkatoProjectRoleItem extends WorkatoItemBase<WorkatoElementType.PROJECT_ROLE> {
  membersCount?: string;
  roleType?: string;
  config?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface WorkatoLegacyRoleItem extends WorkatoItemBase<WorkatoElementType.LEGACY_ROLE> {
  inheritable?: string;
  folderIds?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface WorkatoDeploymentItem extends WorkatoItemBase<WorkatoElementType.DEPLOYMENT> {
  projectId?: string;
  status?: string;
  deployedAt?: string;
  updatedAt?: string;
  environmentType?: string;
  detailedState?: string;
  deploymentTitle?: string;
  deploymentDescription?: string;
  projectBuildId?: string;
  performedByName?: string;
  reviewState?: string;
}

export interface WorkatoProjectGrantItem extends WorkatoItemBase<WorkatoElementType.PROJECT_GRANT> {
  projectId?: string;
  roleId?: string;
  granteeId?: string;
  granteeType?: string;
  userId?: string;
  userGroupId?: string;
  projectRoleName?: string;
}

export interface WorkatoGroupMemberItem extends WorkatoItemBase<WorkatoElementType.GROUP_MEMBER> {
  email?: string;
  role?: string;
  groupId?: string;
}

export type WorkatoConfigurationItem =
  | WorkatoProjectItem
  | WorkatoRecipeItem
  | WorkatoAppItem
  | WorkatoConnectionItem
  | WorkatoConnectorItem
  | WorkatoFolderItem
  | WorkatoJobItem
  | WorkatoCollaboratorItem
  | WorkatoGenieItem
  | WorkatoSkillItem
  | WorkatoKnowledgeBaseItem
  | WorkatoMcpServerItem
  | WorkatoLookupTableItem
  | WorkatoDataTableItem
  | WorkatoEventTopicItem
  | WorkatoCustomConnectorItem
  | WorkatoAuditLogItem
  | WorkatoTagItem
  | WorkatoEnvPropertyItem
  | WorkatoMessageTemplateItem
  | WorkatoCustomOAuthProfileItem
  | WorkatoCollaboratorGroupItem
  | WorkatoEnvRoleItem
  | WorkatoProjectRoleItem
  | WorkatoLegacyRoleItem
  | WorkatoDeploymentItem
  | WorkatoProjectGrantItem
  | WorkatoGroupMemberItem;

export type WorkatoDocumentationListElementsResponse =
  DocumentationListElementsResponse<WorkatoConfigurationItem>;
export type WorkatoDocumentationSearchResponse =
  DocumentationSearchResponse<WorkatoConfigurationItem>;
export type WorkatoDocumentationDependenciesResponse =
  DocumentationDependenciesResponse<WorkatoConfigurationItem>;
export type WorkatoDocumentationDependentsResponse =
  DocumentationDependentsResponse<WorkatoConfigurationItem>;

export interface WorkatoDocumentationChildrenResponse {
  children: Record<string, WorkatoConfigurationItem[]>;
}
